import { useBuildingPackageFileUpload } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { useState } from 'react';

import { Field, SheetForm } from '@/components';
import { FileUpload } from '@/components/file-upload';
import { errorToast, successToast } from '@/components/toast';

import { UploadDocumentFormData } from './types';
import { validateUploadDocumentForm } from './validator';

export const UploadBuildingPackageDocumentForm = () => {
  const { upload: uploadBuildingPackageFiles } = useBuildingPackageFileUpload();

  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = async (): Promise<boolean> => {
    if (isUploading) {
      return false;
    }

    const formData: UploadDocumentFormData = { files };
    const validation = validateUploadDocumentForm(formData);

    if (!validation.isValid) {
      setError(validation.errors.files || 'Please select at least one file to upload');
      return false;
    }

    setIsUploading(true);
    setError(undefined);

    try {
      await uploadBuildingPackageFiles(files);
      successToast({ message: 'Documents uploaded successfully' });
      setFiles([]);
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload documents';
      setError(errorMessage);
      errorToast({ message: errorMessage });
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFiles([]);
    setError(undefined);
  };

  return (
    <SheetForm
      title="Upload Documents"
      description="Select documents to upload to this building package"
      trigger={<Button>Upload Document</Button>}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isSubmitting={isUploading}
      submitLabel={isUploading ? 'Uploading...' : 'Upload'}
    >
      <Field label="Documents" htmlFor="documents" required error={error}>
        <FileUpload disabled={isUploading} files={files} setFiles={setFiles} maxFiles={10} />
      </Field>
    </SheetForm>
  );
};
